import { useEffect, useState, useRef } from "react";
import { doc, onSnapshot, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

// time must differ (ms) to trigger correction
const DRIFT_THRESHOLD = 1000;

export function useSharedTimer(roomId, localTimerState, isAdmin) {
  const [sharedState, setSharedState] = useState(null);
  const lastPush = useRef(0); // prevent write loops

  const roomRef = doc(db, "timers", roomId); 

  // Subscribe to remote changes
  useEffect(() => {
    const unsub = onSnapshot(roomRef, (docSnap) => {
      const data = docSnap.data();
      if (!data) return;

      // Don't react to our own recent update
      if (Date.now() - lastPush.current < 300) return;

      setSharedState(data);
    });
    return () => unsub();
  }, [roomId]);

  // ⏫ Push local state to Firestore if admin
  useEffect(() => {
    if (!isAdmin) return;

    const pushUpdate = async () => {
      lastPush.current = Date.now();
      await updateDoc(roomRef, {
        ...localTimerState,
        lastUpdated: Date.now(),
      });
    };

    pushUpdate();
  }, [localTimerState.running, localTimerState.baseSeconds, localTimerState.timerSeconds]);

  // ⏳ Apply remote state to local timer (for viewers)
  const getCorrectedSeconds = () => {
    if (!sharedState || isAdmin) return null;

    const { lastUpdated, baseSeconds, running } = sharedState;
    const now = Date.now();
    const elapsed = Math.floor((now - lastUpdated) / 1000);
    const corrected = Math.max(0, baseSeconds - elapsed);

    // Only return if drift is meaningful
    if (Math.abs(corrected - localTimerState.timerSeconds) >= DRIFT_THRESHOLD / 1000) {
      return corrected;
    }
    return null;
  };

  return {
    sharedState,
    correctedTimerSeconds: getCorrectedSeconds(),
  };
}
